import { useState } from "react"
import type { Person } from "@/lib/person"
import type { Channel, MentionNames, ReviewAction } from "../types"
import ActionEditor, { type ActionDraft } from "./ActionEditor"
import ActionRow from "./ActionRow"
import Section from "./Section"

interface Props {
  actions: ReviewAction[]
  actionTypes: string[]
  channels: Channel[]
  people: Person[]
  names: MentionNames
  // Each calls back once the server answers; saved is false when it refused,
  // so the form keeps what was typed.
  onCreate: (draft: ActionDraft, done: (saved: boolean) => void) => void
  onUpdate: (id: string, draft: ActionDraft, done: (saved: boolean) => void) => void
  onDelete: (id: string) => void
}

// The actions saved for this product, then, set apart under its own heading,
// a blank form always open for the next one.
export default function ActionsSection({ actions, actionTypes, channels, people, names, onCreate, onUpdate, onDelete }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [blankKey, setBlankKey] = useState(0)
  const [saving, setSaving] = useState(false)

  const editorProps = { actionTypes, channels, people, saving }

  return (
    <Section title={`Actions · ${actions.length}`}>
      {actions.length > 0 ? (
        <div className="divide-y rounded-md border">
          {actions.map((action) =>
            editingId === action.id ? (
              <ActionEditor
                key={action.id}
                action={action}
                {...editorProps}
                onSave={(draft) => { setSaving(true); onUpdate(action.id, draft, (saved) => { setSaving(false); if (saved) setEditingId(null) }) }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <ActionRow
                key={action.id}
                action={action}
                people={people}
                names={names}
                onEdit={() => setEditingId(action.id)}
                onDelete={() => onDelete(action.id)}
              />
            ),
          )}
        </div>
      ) : (
        <div className="rounded-md border px-4 py-3 text-sm">No actions yet.</div>
      )}

      {/* Always open, so the room can type the next action straight away.
          A new key after each save clears it. */}
      <h4 className="mb-2 mt-6 text-sm font-semibold">Add an action</h4>
      <div className="rounded-md border">
        <ActionEditor
          key={`new-${blankKey}`}
          action={null}
          className="rounded-md"
          {...editorProps}
          onSave={(draft) => { setSaving(true); onCreate(draft, (saved) => { setSaving(false); if (saved) setBlankKey((k) => k + 1) }) }}
        />
      </div>
    </Section>
  )
}
